/**
 * Concrete Elation scrape recipe + assembler.
 *
 * This file is where all selector / field / list changes for Elation
 * scraping live. The content script in `extension/content/elationScraper.ts`
 * evaluates whatever we send it; after the extension-rebuild that introduced
 * the recipe pipeline, changes here ship with the normal web deploy — no
 * extension reload needed.
 *
 * To change scraping:
 *   1. Edit `ELATION_SCRAPE_RECIPE` below (selectors, regex patterns,
 *      add/remove fields or lists).
 *   2. Update `assembleElationPageData` if you added a new field or
 *      list key that the UI needs to consume.
 *   3. Deploy the web app. The iframe sends the new recipe to the
 *      sidepanel on next mount.
 */

import type {
  ScrapeRecipe,
  ScrapeResult,
} from "../../extension/content/scrapeRecipe.types";

export const ELATION_SCRAPE_RECIPE: ScrapeRecipe = {
  fields: {
    // Patient clinical-profile panel. data-testids here are the most
    // brittle surface — if Elation restyles the chart header, this is
    // usually the first thing to update.
    patientName: {
      kind: "text",
      selector: '[data-testid="clinical-profile-patient-name-link"]',
    },
    // dob+age lives in one field rendered like "12/10/1953 (72 yrs, 3 mo) English".
    // Split with two regexes against the same node.
    dob: {
      kind: "regex-text",
      selector: '[data-testid="clinical-profile-patient-dob-age-language"]',
      pattern: "^(\\d{1,2}/\\d{1,2}/\\d{4})",
      group: 1,
    },
    age: {
      kind: "regex-text",
      selector: '[data-testid="clinical-profile-patient-dob-age-language"]',
      pattern: "\\(([^)]+)\\)",
      group: 1,
    },
    sex: {
      kind: "text",
      selector: '[data-testid="clinical-profile-patient-sex-at-birth"]',
    },
    primaryPhysician: {
      kind: "text",
      selector: '[data-testid="clinical-profile-patient-primary-physician"]',
    },
    phone: {
      kind: "text",
      selector: '[data-testid="clinical-profile-patient-phone"]',
    },
    // Risk-score badge — Elation renders several badges, we only want the
    // one whose text starts with "RISK". Handle the prefix strip below in
    // the assembler since the recipe can't do conditional selection.
    riskScoreBadge: {
      kind: "regex-text",
      // Match any badge element; the assembler picks out the RISK one.
      // TODO: if Elation's badge class name changes again, update here.
      selector: '[class*="TempPatientProfileBadge__badge"]',
      pattern: "^(RISK.*)",
      group: 1,
    },
    // URL-scoped — always available regardless of DOM state, so the
    // demo-member hardcode can fire from any patient-scoped Elation
    // route (docs, reports, the chart itself).
    ehrPatientId: {
      kind: "regex-pathname",
      pattern: "/patient/(\\d+)(?:/|$)",
      group: 1,
    },
  },
  lists: {
    problemList: { items: "#active-problem-summary li", as: "text" },
    allergies: { items: "#allergy-summary li", as: "text" },
    medicationsRx: { items: "#perm-rx-meds-summary li", as: "text" },
    medicationsOtc: { items: "#perm-otc-meds-summary li", as: "text" },
    // Secondary clinical lists (DOM-only — no JSON endpoint observed for
    // these). Best-effort: fail soft to empty when the chart omits them.
    intolerances: { items: "#intol-summary li", as: "text" },
    immunizations: { items: "#immunization-history-list li", as: "text" },
    pastProblems: { items: "#other-problem-summary li", as: "text" },
  },
};

export interface ElationPatientData {
  patientName: string;
  firstName: string;
  lastName: string;
  dob: string;
  age: string;
  sex: string;
  primaryPhysician: string;
  phone: string;
  ehrPatientId: string | null;
  riskScore: string | null;
  /** Which EHR this chart was scraped from (adapter id, e.g. "EMR_ECW"). */
  sourceSystem: string;
}

export interface AssembledElationPageData {
  patient: ElationPatientData | null;
  problemList: string[];
  allergies: string[];
  medications: string[];
  careGaps: string[];
  intolerances: string[];
  immunizations: string[];
  pastProblems: string[];
  insurance: string[];
}

/**
 * Convert the raw evaluator output into the shape the rest of the app
 * expects. Field / list keys here must match the recipe above.
 */
export function assembleElationPageData(
  result: ScrapeResult,
): AssembledElationPageData {
  const f = result.fields;
  const fullName = f.patientName ?? "";
  // `ehrPatientId` was renamed from `elationPatientId`. The recipe key travels
  // web-app → extension at runtime and the two deploy on independent cadences,
  // so tolerate an older recipe still emitting the old key during the skew
  // window. Safe to drop the fallback once both surfaces are past this release.
  const ehrPatientId = f.ehrPatientId ?? f.elationPatientId ?? null;
  // Prefer discrete first/last names when the adapter provides them (eCW reads
  // FNAME/LNAME as separate fields). Splitting a merged name on whitespace is
  // lossy — a two-word first name or last name lands in the wrong bucket — so
  // the split is only a fallback for adapters (Elation) that scrape one string.
  const discreteFirst = f.firstName ?? "";
  const discreteLast = f.lastName ?? "";
  const hasAnyPatientSignal =
    !!fullName || !!discreteFirst || !!discreteLast || !!ehrPatientId;

  let patient: ElationPatientData | null = null;
  if (hasAnyPatientSignal) {
    let firstName = discreteFirst;
    let lastName = discreteLast;
    if (!firstName && !lastName && fullName) {
      const nameParts = fullName.split(/\s+/);
      firstName = nameParts[0] ?? "";
      lastName = nameParts.slice(1).join(" ");
    }
    const displayName =
      fullName || [firstName, lastName].filter(Boolean).join(" ").trim();
    const riskBadge = f.riskScoreBadge ?? "";
    const riskScore = riskBadge.startsWith("RISK")
      ? riskBadge.replace(/^RISK\s*/, "").trim() || null
      : null;

    patient = {
      patientName: displayName,
      firstName,
      lastName,
      dob: f.dob ?? "",
      age: f.age ?? "",
      sex: f.sex ?? "",
      primaryPhysician: f.primaryPhysician ?? "",
      phone: f.phone ?? "",
      ehrPatientId,
      riskScore,
      // Which EHR matched. Default to Elation for older extensions / recipes
      // that don't emit this field yet.
      sourceSystem: f.sourceSystem || "EMR_ELATION",
    };
  }

  const rx = cleanList(result.lists.medicationsRx);
  const otc = cleanList(result.lists.medicationsOtc);

  return {
    patient,
    problemList: cleanList(result.lists.problemList),
    allergies: cleanList(result.lists.allergies),
    medications: [...rx, ...otc],
    careGaps: toStringList(result.lists.careGaps),
    intolerances: cleanList(result.lists.intolerances),
    immunizations: cleanList(result.lists.immunizations),
    pastProblems: cleanList(result.lists.pastProblems),
    insurance: toStringList(result.lists.insurance),
  };
}

/**
 * Elation renders each clinical-summary row (`#...-summary li`) as a cluster of
 * inline action controls with NO separating whitespace, so the row's raw
 * textContent concatenates the chrome into the value. The chrome varies by
 * list (confirmed against the live chart DOM):
 *   - problems:   "Actions{year}{name} [{code}]Last addressed: …Show Synopsis…Hide"
 *   - medications:"{count}Actions{MM/YYYY}{name}"
 *   - allergies:  "Actions{name}"
 * Strip all of that down to the clinical label. Endpoint-derived strings carry
 * none of these markers, so they pass through unchanged.
 */
export function cleanSummaryRow(raw: string): string {
  // 1. Drop the trailing metadata / synopsis chrome (everything from the first
  //    marker onward): "Last addressed: …", the synopsis toggle + its body.
  let s = raw.replace(/(Last addressed:|Show Synopsis)[\s\S]*$/i, "");
  // 2. Strip leading row chrome: an optional item count, the "Actions" control
  //    label, then an optional onset date (YYYY, MM/YYYY, or MM/DD/YYYY).
  s = s.replace(/^\s*\d*\s*Actions\s*/i, "");
  s = s.replace(/^\s*(?:\d{1,2}\/){0,2}(?:19|20)\d{2}\s*(?=\S)/, "");
  // 3. Any residual standalone toggle labels left mid-string.
  s = s.replace(/\b(?:Show Synopsis|Hide)\b/gi, " ");
  return s.replace(/\s+/g, " ").trim();
}

/** Scrape-list -> cleaned, non-empty string[] (drops chrome-only rows). */
export function cleanList(
  value: ScrapeResult["lists"][string] | undefined,
): string[] {
  return toStringList(value)
    .map(cleanSummaryRow)
    .filter((s) => s.length > 0);
}

function toStringList(
  value: ScrapeResult["lists"][string] | undefined,
): string[] {
  if (!value) return [];
  if (value.length === 0) return [];
  if (typeof value[0] === "string") return value as string[];
  // `as: "fields"` — the assembler for this recipe never uses the
  // structured form, but guard against a misconfigured entry silently
  // corrupting output.
  return [];
}
